import type { ClientContactCreated, ClientContactView } from "@closer/shared";
import { createClientContactSchema, updateClientContactSchema } from "@closer/shared";
import { Badge, Button, Card, Checkbox, FormField, Input, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

/**
 * MÜVEKKİL İLETİŞİM KİŞİLERİ (CLOS-174 `[121]`).
 *
 * ### ⚠ "Temsilci" DEMİYOR, "temsilci ADAYI" diyor
 *
 * Kartın bağımlılık sınırı: işaret yalnız davet için bir seçim kolaylığı; temsil yetkisi
 * davet kabul edilene kadar YOK. Ekranda "Temsilci" yazsaydı, kullanıcı kutucuğu
 * işaretleyerek birine yetki verdiğini sanırdı — üç ayrı kavram (iletişim kişisi, hesap,
 * temsil yetkisi) tek kelimede erirdi.
 *
 * ### ⚠ Yineleme uyarısı ENGELLEMİYOR
 *
 * Kayıt yapılıyor, uyarı sonradan gösteriliyor: aynı ofis adresini iki kişi için kullanmak
 * olağan bir durum ve engelleseydik kullanıcıyı sahte adres uydurmaya iterdik.
 */

export function ClientContacts({ clientId, canManage }: { clientId: string; canManage: boolean }) {
  const { activeTenantId } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();
  const [formAcik, setFormAcik] = useState(false);
  const [uyarilar, setUyarilar] = useState<string[]>([]);

  const form = useZodForm(createClientContactSchema);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "client-contacts", clientId),
    queryFn: async () =>
      (await api<ClientContactView[]>(`/api/clients/${clientId}/contacts`, { tenantId })).data,
  });

  async function tazele() {
    await queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "client-contacts", clientId),
    });
  }

  const ekle = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<ClientContactCreated>(`/api/clients/${clientId}/contacts`, {
        method: "POST",
        tenantId,
        body: JSON.stringify(girdi),
      }),
    onSuccess: async (yanit) => {
      setFormAcik(false);
      form.reset();
      /** ⚠ Uyarı işlemden SONRA: engel değil, kullanıcıyı bilgilendirme. */
      setUyarilar(
        yanit.data.duplicates.map(
          (d) =>
            `${d.fullName} kaydıyla aynı ${d.matchedOn === "email" ? "e-posta" : "telefon"} bilgisi kullanılıyor.`,
        ),
      );
      await tazele();
      toast.success("İletişim kişisi eklendi");
    },
    onError: (hata) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(form, hata);
        return;
      }
      toast.error(hata instanceof ApiRequestError ? hata.message : "İletişim kişisi eklenemedi");
    },
  });

  const pasiflestir = useMutation({
    mutationFn: (kisi: ClientContactView) =>
      api<ClientContactView>(`/api/clients/${clientId}/contacts/${kisi.id}`, {
        method: "PATCH",
        tenantId,
        body: JSON.stringify({ version: kisi.version, status: "inactive" }),
      }),
    onSuccess: async () => {
      await tazele();
      toast.success("İletişim kişisi pasifleştirildi");
    },
    onError: (hata) => {
      toast.error(hata instanceof ApiRequestError ? hata.message : "İşlem tamamlanamadı");
    },
  });

  /**
   * DÜZENLEME (CLOS-866).
   *
   * ### ⚠ Ucun YARISI kullanılmıyordu
   *
   * `updateClientContactSchema` ad, görev, e-posta, telefon ve aday işaretinin
   * güncellenmesine izin veriyor ve uç testli; ekran ise yalnız `status: "inactive"`
   * gönderiyordu. Sonuç: kullanıcı bir yazım hatasını düzeltmek için kişiyi pasifleştirip
   * yeniden eklemek zorundaydı ve tarihçe her seferinde kirleniyordu.
   *
   * ### ⚠ SÜRÜM GÖNDERİLİYOR — ve 409 sessizce yutulmuyor
   *
   * Uç iyimser kilit kullanıyor. İki kişi aynı kaydı düzenlerse ikincinin yazısı
   * birincisini EZMEMELİ: çakışmada kayıt yeniden okunuyor ve kullanıcıya söyleniyor
   * (CLOS-170 deseni). Körlemesine tekrar göndermek, başkasının değişikliğini yok etmek
   * olurdu.
   */
  const [duzenlenen, setDuzenlenen] = useState<ClientContactView | null>(null);
  const duzenleForm = useZodForm(updateClientContactSchema);

  const duzenle = useMutation({
    mutationFn: (girdi: Record<string, unknown>) =>
      api<ClientContactView>(`/api/clients/${clientId}/contacts/${duzenlenen!.id}`, {
        method: "PATCH",
        tenantId,
        body: JSON.stringify({ ...girdi, version: duzenlenen!.version }),
      }),
    onSuccess: async () => {
      setDuzenlenen(null);
      await tazele();
      toast.success("İletişim kişisi güncellendi");
    },
    onError: async (hata) => {
      if (hata instanceof ApiRequestError && hata.fields) {
        applyServerErrors(duzenleForm, hata);
        return;
      }
      /** ⚠ Çakışmada KAYIT YENİDEN OKUNUYOR: kullanıcı güncel hâli görüp kararını verir. */
      if (hata instanceof ApiRequestError && hata.code === "CONCURRENCY_CONFLICT") {
        await tazele();
        setDuzenlenen(null);
        toast.error("Bu kayıt siz düzenlerken değişti; güncel hâli yüklendi.");
        return;
      }
      toast.error(
        hata instanceof ApiRequestError ? hata.message : "İletişim kişisi güncellenemedi",
      );
    },
  });

  function duzenlemeyiAc(kisi: ClientContactView) {
    setDuzenlenen(kisi);
    duzenleForm.reset({
      version: kisi.version,
      fullName: kisi.fullName,
      title: kisi.title ?? "",
      email: kisi.email ?? "",
      phone: kisi.phone ?? "",
      isRepresentativeCandidate: kisi.isRepresentativeCandidate,
    });
  }

  /**
   * Alan hatasının METNİ.
   *
   * ⚠ `updateClientContactSchema`'da bu alanlar nullable birleşim olduğu için react-hook-form
   * hata tipini `FieldError | Merge<...>` olarak genişletiyor ve `message` her zaman dize
   * olmuyor. Cast etmek yerine daraltıyoruz: dize değilse hata METNİ yok demektir ve
   * `FormField` boş geçmelidir — `String(hata)` deseydik kullanıcıya `[object Object]`
   * gösterebilirdik.
   */
  function hataMetni(hata: unknown): string | undefined {
    return typeof hata === "string" ? hata : undefined;
  }

  const duzenlemeyiGonder = duzenleForm.handleSubmit((girdi) =>
    duzenle.mutate(girdi as Record<string, unknown>),
  );

  const gonder = form.handleSubmit((girdi) => ekle.mutate(girdi as Record<string, unknown>));

  return (
    <Card>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <h2 className="mr-auto text-base font-semibold text-fg-heading">İletişim kişileri</h2>
          {canManage && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setFormAcik((a) => !a)}
            >
              {formAcik ? "Vazgeç" : "Kişi ekle"}
            </Button>
          )}
        </div>

        {uyarilar.map((uyari) => (
          <p key={uyari} className="text-xs text-fg-soft">
            {uyari}
          </p>
        ))}

        {formAcik && (
          <form noValidate className="flex flex-col gap-2" onSubmit={gonder}>
            <FormField label="Ad soyad" error={form.formState.errors.fullName?.message} required>
              <Input {...form.register("fullName")} />
            </FormField>
            <FormField label="Görev" error={form.formState.errors.title?.message}>
              <Input {...form.register("title")} />
            </FormField>
            <FormField label="E-posta" error={form.formState.errors.email?.message}>
              <Input type="email" {...form.register("email")} />
            </FormField>
            <FormField label="Telefon" error={form.formState.errors.phone?.message}>
              <Input {...form.register("phone")} />
            </FormField>
            {/*
              ⚠ Etiket "temsilci ADAYI" — kutucuk yetki VERMİYOR, yalnız ileride davet
              ekranında seçimi kolaylaştırıyor.
            */}
            <Checkbox
              label="Temsilci adayı olarak işaretle (yetki vermez)"
              {...form.register("isRepresentativeCandidate")}
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" loading={ekle.isPending}>
                Ekle
              </Button>
            </div>
          </form>
        )}

        {sorgu.isPending ? (
          <div role="status" aria-label="İletişim kişileri yükleniyor">
            <Skeleton lines={2} />
          </div>
        ) : sorgu.error ? (
          <StateView
            state={resolveErrorState(sorgu.error, { idempotent: true })}
            onRetry={() => void sorgu.refetch()}
          />
        ) : (sorgu.data ?? []).length === 0 ? (
          <p className="text-sm text-fg-soft">Bu müvekkil için iletişim kişisi kaydedilmemiş.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(sorgu.data ?? []).map((kisi) => (
              <Fragment key={kisi.id}>
                <li className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-fg-strong">{kisi.fullName}</span>
                  {kisi.title && <span className="text-xs text-fg-soft">{kisi.title}</span>}
                  {kisi.isRepresentativeCandidate && <Badge tone="neutral">Temsilci adayı</Badge>}
                  <span className="text-xs text-fg-soft">{kisi.email ?? kisi.phone ?? "—"}</span>
                  {canManage && (
                    <span className="ml-auto flex items-center gap-1">
                      {/*
                      ⚠ Düzenle PASİFLEŞTİRMEYLE AYNI yetkide (`client.edit`): ikisi de
                      müvekkilin iletişim bilgisine dokunuyor. Ayrı bir anahtar açmak,
                      "kişiyi pasifleştirebilen ama telefonunu düzeltemeyen" gibi kimsenin
                      istemediği bir rol üretirdi.
                    */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => duzenlemeyiAc(kisi)}
                      >
                        Düzenle
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => pasiflestir.mutate(kisi)}
                      >
                        Pasifleştir
                      </Button>
                    </span>
                  )}
                </li>
                {duzenlenen?.id === kisi.id ? (
                  <li>
                    <form
                      noValidate
                      data-testid="kisi-duzenle-formu"
                      className="flex flex-col gap-2 rounded-md border border-border-default p-3"
                      onSubmit={duzenlemeyiGonder}
                    >
                      <FormField
                        label="Ad soyad"
                        error={hataMetni(duzenleForm.formState.errors.fullName?.message)}
                        required
                      >
                        <Input {...duzenleForm.register("fullName")} />
                      </FormField>
                      <FormField
                        label="Görev"
                        error={hataMetni(duzenleForm.formState.errors.title?.message)}
                      >
                        <Input {...duzenleForm.register("title")} />
                      </FormField>
                      <FormField
                        label="E-posta"
                        error={hataMetni(duzenleForm.formState.errors.email?.message)}
                      >
                        <Input type="email" {...duzenleForm.register("email")} />
                      </FormField>
                      <FormField
                        label="Telefon"
                        error={hataMetni(duzenleForm.formState.errors.phone?.message)}
                      >
                        <Input {...duzenleForm.register("phone")} />
                      </FormField>
                      <Checkbox
                        label="Temsilci adayı olarak işaretle (yetki vermez)"
                        {...duzenleForm.register("isRepresentativeCandidate")}
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => setDuzenlenen(null)}
                        >
                          Vazgeç
                        </Button>
                        <Button type="submit" size="sm" loading={duzenle.isPending}>
                          Kaydet
                        </Button>
                      </div>
                    </form>
                  </li>
                ) : null}
              </Fragment>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
